
import { Options, Vue } from 'vue-class-component'
import CLoading from '@/components/common/ui/CLoading.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { IForm, IValidRequest } from '@/utils/types'
import { ACTION_APP, ACTION_FORM } from '@/store/actions'
import cloneDeep from 'lodash/cloneDeep'
import { Formater } from '@/utils/formater'
import TagContent from '@/components/common/ui/TagContent.vue'
import FormFormModal from '@/components/form/FormFormModal.vue'
import { maska } from 'maska'
import { FORM_FIELD_TYPE, RESOURCE_TYPE } from '@/utils/constants'
import { lastNMonth, now } from '@/utils/datetime'
import AppCopyButton from '@/components/common/ui/buttons/AppCopyButton.vue'

@Options({
  components: { FormFormModal, TagContent, CLoading, AppCopyButton },
  directives: { maska },
})
export default class Forms extends Vue {
  @Prop()
  appId!: string

  modalVisible = false
  loading = true

  selectedForm: IForm = {}

  get forms(): IForm[] {
    return this.$store.getters.forms
  }

  @Watch('appId', { immediate: true })
  async appIdChanged() {
    if (!this.appId) {
      return
    }

    await this.$store.dispatch(ACTION_FORM.LOAD_ITEMS, {
      app_id: this.appId,
      is_include_data: true,
    })

    this.loading = false
  }

  onAdd() {
    this.selectedForm = {
      app_id: this.appId,
    }

    this.modalVisible = true
  }

  onCopy(data: IForm) {
    const formCopy = cloneDeep(data)
    formCopy._id = ''
    formCopy.title = formCopy.title + ' Copy'
    formCopy.liff_id = ''
    formCopy.liff_id_test = ''
    formCopy.display_id = ''
    this.selectedForm = formCopy
    this.modalVisible = true
  }

  onEdit(data: IForm) {
    this.selectedForm = data
    this.modalVisible = true
  }

  async onDelete(form: IForm) {
    this.$q
      .dialog({
        title: this.$t('confirm'),
        message: this.$t('are_you_sure_you_want_to_delete'),
        cancel: true,
        persistent: true,
      })
      .onOk(async () => {
        const exists = await this.checkItemExists(form)
        if (!exists) {
          const success = await this.$store.dispatch(ACTION_FORM.DELETE, form._id)
          if (success) {
            this.$q.notify({
              message: this.$t('messages.deleted'),
              color: 'positive',
            })
          }
        }
      })
  }

  onCloseModal() {
    this.modalVisible = false
    this.selectedForm = {}
  }

  numberWithCommas(num) {
    return Formater.numberFormat(num)
  }

  optionTypeName(option_type) {
    if (option_type === FORM_FIELD_TYPE.NAME_KANJI) {
      return this.$t('label.common.form.form_field_type_name_kanji')
    } else if (option_type === FORM_FIELD_TYPE.NAME_HIRA) {
      return this.$t('label.common.form.form_field_type_name_hira')
    } else if (option_type === FORM_FIELD_TYPE.NAME_KANA) {
      return this.$t('label.common.form.form_field_type_name_kana')
    } else if (option_type === FORM_FIELD_TYPE.PHONE_NUMBER_DASH) {
      return this.$t('label.common.form.form_field_type_phone_number_dash')
    } else if (option_type === FORM_FIELD_TYPE.PHONE_NUMBER_NO_DASH) {
      return this.$t('label.common.form.form_field_type_phone_number_no_dash')
    } else if (option_type === FORM_FIELD_TYPE.ADDRESS) {
      return this.$t('label.common.form.form_field_type_address')
    } else if (option_type === FORM_FIELD_TYPE.POSTCODE_DASH) {
      return this.$t('label.common.form.form_field_type_postcode_dash')
    } else if (option_type === FORM_FIELD_TYPE.POSTCODE_NO_DASH) {
      return this.$t('label.common.form.form_field_type_postcode_no_dash')
    } else if (option_type === FORM_FIELD_TYPE.EMAIL) {
      return this.$t('label.common.form.form_field_type_email')
    }
    return this.$t('label.common.form.form_field_type_freetext')
  }

  async checkItemExists(form: IForm) {
    const { dispatch } = this.$store
    const params: IValidRequest = {
      app_id: this.appId,
      initial_id: '',
      push_id: '',
      card_id: form._id ?? '',
      card_type: 'form',
      total_messages: 0,
      campaign: {},
    }
    const resp = await dispatch(ACTION_APP.CHECK_ITEM_EXISTS, params)

    if (resp.campaign_title) {
      this.$q.dialog({
        title: this.$t('error'),
        message: this.$t('messages.item_exists_in_campaign', { names: resp.campaign_title.toString() }),
        persistent: true,
        html: true,
      })
      return true
    }
    return false
  }

  onDetails(event, data: IForm) {
    // Check if Ctrl (Windows) or Meta (Mac) is pressed
    if (event.ctrlKey || event.metaKey) {
      // Let the browser handle opening in a new tab
      return
    }
    // Otherwise, navigate programmatically
    event.preventDefault()
    this.goto('form_details', {
      app_id: this.appId,
      asset_type: RESOURCE_TYPE.FORM,
      asset_id: data._id,
      from_date: lastNMonth(1),
      to_date: now(),
    })
  }

  getDetailsUrl(data) {
    // Construct the URL as a string for direct navigation
    return this.$router.resolve({
      name: 'form_details',
      params: {
        app_id: this.appId,
        asset_type: RESOURCE_TYPE.FORM,
        asset_id: data._id,
        from_date: lastNMonth(1),
        to_date: now(),
      },
    }).href
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }
}
